// BLU-590 — Designs Source settings (per-ORG hands-off print-file self-heal).
//
// When a print file is missing and no line-item URL exists, the ERP can fetch
// the design PNG BY DESIGN-ID from this org's Google Drive designs folder,
// resize it for the platen, and heal the issue automatically. The operator's
// only job is keeping correctly-named PNGs in the folder + sharing it to the
// service account below.
//
// Per-ORG config (prod_settings, one row per org) — NOT per-location — because
// the designs belong to the client org whose order it is. Mirrors the
// load/dirty/save shape of PrintFileSelfHealSection and the SA-email + Test
// affordances of GcsCloudStorageSection. Plain English strings (internal admin
// panel), matching GcsCloudStorageSection's mixed t()/literal style.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RIP_CODE_REGEX } from '@threadheads/shared';
import { useOwnerClientTarget } from '../hooks/useOwnerClientTarget';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PrintSizeCodeCombobox } from './PrintSizeCodeCombobox';
import { buildProductTypeCodeOptions, codeTablesStatus, joinStoreNames } from './org-code-labels';
import {
  Loader2,
  Save,
  KeyRound,
  Copy,
  Check,
  PlugZap,
  CheckCircle2,
  XCircle,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

const FOLDER_ID_RE = /^[A-Za-z0-9_-]{10,128}$/;

type TestResult =
  | { ok: true; fileCount: number; sampleNames: string[] }
  | { ok: false; reason: 'sa_not_configured' | 'not_shared_or_not_found' | 'unreachable' | 'rate_limited' }
  | null;

const TEST_FAIL_MESSAGE: Record<string, string> = {
  sa_not_configured:
    'The Google Drive API is not reachable for our service account. Ask an administrator to enable the Drive API on the service-account project.',
  not_shared_or_not_found:
    'Folder not found. Check the folder id, and share the folder with the service account below (as Viewer).',
  unreachable: 'Could not reach Google Drive. Try again in a moment.',
  rate_limited: 'Too many test attempts. Wait a minute and try again.',
};

// Short label for the persistent inline result chip (the toast carries the
// full guidance above). Covers every failure reason so nothing falls through
// to a misleading default.
const TEST_CHIP_LABEL: Record<string, string> = {
  sa_not_configured: 'Service account cannot reach Drive',
  not_shared_or_not_found: 'Not found or not shared',
  unreachable: 'Unreachable',
  rate_limited: 'Rate limited, try again shortly',
};

interface DesignsSourceSectionProps {
  /** Reserved for parity with sibling sections (renders inline either way). */
  embedded?: boolean;
}

export function DesignsSourceSection(_props: DesignsSourceSectionProps): JSX.Element {
  const { t } = useTranslation('settings');
  const {
    ownOrgId,
    clientOptions,
    selectedOrgId,
    setSelectedOrgId,
    targetOrgInput: targetOrgId,
  } = useOwnerClientTarget();

  return (
    <div className="space-y-5">
      {clientOptions.length > 0 && (
        <div className="grid max-w-xs gap-1.5">
          <Label htmlFor="designs-source-client">{t('production.designsSource.appliesTo')}</Label>
          <Select value={selectedOrgId ?? ownOrgId} onValueChange={setSelectedOrgId} disabled={!ownOrgId}>
            <SelectTrigger id="designs-source-client" className="h-11">
              <SelectValue placeholder={t('production.designsSource.selectOrganization')} />
            </SelectTrigger>
            <SelectContent>
              {ownOrgId && <SelectItem value={ownOrgId}>{t('production.designsSource.thisOrganization')}</SelectItem>}
              {clientOptions.map(client => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Remount isolates draft and in-flight test results when the target changes. */}
      {selectedOrgId && <DesignsSourceForm key={selectedOrgId} organizationId={targetOrgId} />}
    </div>
  );
}

function DesignsSourceForm({ organizationId }: { organizationId?: string }): JSX.Element {
  const { t } = useTranslation('settings');
  const settingsQuery = trpc.productionSettings.getDesignsSourceSettings.useQuery({ organizationId });
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery({ organizationId });

  const [enabled, setEnabled] = useState(false);
  const [folderId, setFolderId] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [testResult, setTestResult] = useState<TestResult>(null);
  const [showSamples, setShowSamples] = useState(false);

  // Hydrate the form from the server once loaded / after a save-refetch.
  useEffect(() => {
    if (settingsQuery.data) {
      setEnabled(settingsQuery.data.enabled);
      setFolderId(settingsQuery.data.folderId ?? '');
      setOverrides(settingsQuery.data.overrides);
      setHasChanges(false);
    }
  }, [settingsQuery.data]);

  const updateMutation = trpc.productionSettings.updateDesignsSourceSettings.useMutation({
    onSuccess: () => {
      toast.success('Designs source saved');
      setHasChanges(false);
      void settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Could not save: ${error.message}`);
    },
  });

  const testMutation = trpc.productionSettings.testDesignsFolder.useMutation({
    onSuccess: (res) => {
      setTestResult(res);
      setShowSamples(false);
      if (res.ok) {
        toast.success(`Folder reachable: ${res.fileCount} PNG${res.fileCount === 1 ? '' : 's'} found`);
      } else {
        toast.error(TEST_FAIL_MESSAGE[res.reason] ?? 'Folder test failed');
      }
    },
    onError: (error) => {
      setTestResult({ ok: false, reason: 'unreachable' });
      toast.error(error.message);
    },
  });

  const productTypeCodes = useMemo(() => Object.keys(overrides).sort(), [overrides]);
  const { typeOptions, typeStoreNamesByCode } = useMemo(() => buildProductTypeCodeOptions(
    codeTablesQuery.data?.productTypes, productTypeCodes, {
      custom: t('production.printSizeRules.badgeCustom'),
      singleSize: t('production.printSizeRules.badgeSingleSize'),
      alreadyAdded: t('production.printSizeRules.alreadyAdded'),
      invalidCode: t('production.codeOptions.invalidCode'),
    },
  ), [codeTablesQuery.data, productTypeCodes, t]);
  const discoveryStatus = codeTablesStatus(codeTablesQuery, typeOptions.length);
  // This surface must remain editable during discovery. Preserve the primitive's
  // pinned loading-disable behavior and choose its usable manual fallback here.
  // Manual code entry stays available during loading/empty/error; only a real
  // discovery failure shows the "suggestions unavailable" hint (never during a
  // normal load, never sticking for an org with no product types yet).
  const pickerStatus = discoveryStatus === 'loaded' ? 'loaded' : 'error';
  const addOverride = (value: string): boolean => {
    const code = value.trim().toUpperCase();
    if (!RIP_CODE_REGEX.test(code)) {
      toast.error(t('production.codeOptions.invalidCode'));
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(overrides, code)) {
      toast.error(t('production.printSizeRules.alreadyAdded'));
      return false;
    }
    setOverrides(previous => ({ ...previous, [code]: '' }));
    setHasChanges(true);
    return true;
  };

  const serviceAccountEmail = settingsQuery.data?.serviceAccountEmail ?? null;
  const trimmedFolder = folderId.trim();
  const folderValid = FOLDER_ID_RE.test(trimmedFolder);
  const folderInvalid = trimmedFolder.length > 0 && !folderValid;

  const copyEmail = (): void => {
    if (!serviceAccountEmail) return;
    navigator.clipboard.writeText(serviceAccountEmail).then(
      () => {
        setEmailCopied(true);
        window.setTimeout(() => setEmailCopied(false), 2000);
      },
      () => toast.error('Could not copy. Select the text and copy manually.'),
    );
  };

  const saveDisabled =
    !hasChanges ||
    updateMutation.isPending ||
    folderInvalid ||
    Object.values(overrides).some(value => !FOLDER_ID_RE.test(value.trim())) ||
    (enabled && !folderValid && productTypeCodes.length === 0);

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status" aria-busy="true">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading designs-source settings…
      </div>
    );
  }

  if (settingsQuery.isError) {
    return <div className="space-y-2">
      <p role="alert" className="text-sm text-destructive">{t('production.designsSource.loadError')}</p>
      <Button variant="outline" onClick={() => void settingsQuery.refetch()}>{t('production.designsSource.retry')}</Button>
    </div>;
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Automatically heal a missing print file by fetching its design PNG from this organization's Google
        Drive designs folder, resizing it for the platen, and uploading it, with no manual upload needed.
        Files must be named so the name contains the design id (e.g.{' '}
        <code className="font-mono">Skull Rose - TH606L.png</code>).
      </p>

      {/* Enable toggle */}
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="pr-4">
          <Label htmlFor="designs-source-enabled" className="font-medium">
            Enable designs source
          </Label>
          <p className="text-sm text-muted-foreground">
            When on, missing print files are healed hands-off from the folder below.
          </p>
        </div>
        <Switch
          id="designs-source-enabled"
          checked={enabled}
          onCheckedChange={(v) => {
            setEnabled(v);
            setHasChanges(true);
          }}
        />
      </div>

      {/* Folder id */}
      <div className="space-y-1.5">
        <Label htmlFor="designs-folder-id">{t('production.designsSource.allOtherProducts')}</Label>
        <Input
          id="designs-folder-id"
          value={folderId}
          disabled={testMutation.isPending}
          placeholder="1AbC2dEfGhIjKlMnOpQrStUvWxYz012345"
          className="font-mono"
          aria-invalid={folderInvalid || undefined}
          aria-describedby="designs-folder-id-hint"
          onChange={(e) => {
            setFolderId(e.target.value);
            setHasChanges(true);
            setTestResult(null);
          }}
        />
        {/* One described helper that swaps hint <-> error in place (reserves its
            height so appearing errors don't reflow the form). */}
        <p
          id="designs-folder-id-hint"
          className={`min-h-[1rem] text-xs ${folderInvalid ? 'text-destructive' : 'text-muted-foreground'}`}
          role={folderInvalid ? 'alert' : undefined}
        >
          {folderInvalid ? (
            "That doesn't look like a Drive folder ID."
          ) : (
            <>
              The part of the folder's URL after <code className="font-mono">/folders/</code>. Letters, digits,{' '}
              <code className="font-mono">_</code> and <code className="font-mono">-</code>, 10 to 128
              characters.
            </>
          )}
        </p>
      </div>

      {/* Share-to service account */}
      <div className="space-y-1.5">
        <p className="text-sm font-medium">Share the folder with this service account (as Viewer)</p>
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {serviceAccountEmail ? (
                <code className="truncate font-mono text-sm text-foreground" title={serviceAccountEmail}>
                  {serviceAccountEmail}
                </code>
              ) : (
                <span className="text-sm italic text-muted-foreground">
                  Service account principal unavailable. Check the server's Google credentials.
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={copyEmail}
              disabled={!serviceAccountEmail || emailCopied}
              className="h-8 gap-1.5 text-xs"
              aria-label="Copy service account principal"
            >
              {emailCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Test folder */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!folderValid || testMutation.isPending}
          onClick={() => {
            setTestResult(null);
            testMutation.mutate({ organizationId, folderId: trimmedFolder });
          }}
        >
          {testMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <PlugZap className="h-4 w-4" aria-hidden="true" />
          )}
          Test folder
        </Button>

        {testResult?.ok === true && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700" role="status">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            {testResult.fileCount} PNG{testResult.fileCount === 1 ? '' : 's'} found
            {testResult.sampleNames.length > 0 && (
              <button
                type="button"
                className="ml-1 inline-flex items-center gap-0.5 rounded-sm underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50"
                onClick={() => setShowSamples((s) => !s)}
                aria-expanded={showSamples}
                aria-controls="designs-sample-names"
              >
                samples
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${showSamples ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
            )}
          </span>
        )}
        {testResult?.ok === false && (
          <span className="inline-flex items-center gap-1 text-xs text-destructive" role="status">
            <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
            {TEST_CHIP_LABEL[testResult.reason] ?? TEST_CHIP_LABEL.unreachable}
          </span>
        )}
      </div>

      {testResult?.ok === true && showSamples && testResult.sampleNames.length > 0 && (
        <ul id="designs-sample-names" className="rounded-md border bg-muted/50 p-3 font-mono text-xs text-muted-foreground">
          {testResult.sampleNames.map((name, i) => (
            <li key={`${name}-${i}`} className="truncate" title={name}>
              {name}
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-medium">{t('production.designsSource.byProductType')}</h3>
        <p className="text-xs text-muted-foreground">{t('production.designsSource.overrideHint')}</p>
        {productTypeCodes.map(code => (
          <DesignsFolderOverrideRow
            key={code}
            code={code}
            storeName={joinStoreNames(typeStoreNamesByCode.get(code) ?? [])}
            folderId={overrides[code] ?? ''}
            organizationId={organizationId}
            onChange={value => {
              setOverrides(previous => ({ ...previous, [code]: value }));
              setHasChanges(true);
            }}
            onRemove={() => {
              setOverrides(previous => {
                const next = { ...previous };
                delete next[code];
                return next;
              });
              setHasChanges(true);
            }}
          />
        ))}
        <PrintSizeCodeCombobox
          options={typeOptions}
          status={pickerStatus}
          onSelect={addOverride}
          placeholder={t('production.printSizeRules.typeComboboxPlaceholder')}
          ariaLabel={t('production.printSizeRules.typeComboboxAriaLabel')}
          addButtonLabel={t('production.printSizeRules.addProductType')}
          createLabel={code => t('production.printSizeRules.createCode', { code })}
          emptyLabel={t('production.codeOptions.noMatches')}
          errorHint={discoveryStatus === 'error' ? t('production.designsSource.manualCodeHint') : undefined}
        />
      </div>

      {/* Last tested */}
      {settingsQuery.data?.lastTestAt && (
        <p className="text-xs text-muted-foreground">
          Last tested {new Date(settingsQuery.data.lastTestAt).toLocaleString()}
          {settingsQuery.data.lastTestOutcome ? ` · ${settingsQuery.data.lastTestOutcome}` : ''}
        </p>
      )}

      {/* Save */}
      {hasChanges && (
        <div className="flex items-center justify-end gap-3">
          {/* Programmatic reason the Save is disabled — colour alone isn't
              enough (an enabled toggle with an empty folder blocks the save).
              Only the EMPTY case needs this; the malformed case is already
              named by the red inline field hint. */}
          {enabled && trimmedFolder.length === 0 && productTypeCodes.length === 0 && (
            <span className="text-xs text-amber-700" role="status">
              Add a folder ID to turn this on
            </span>
          )}
          <Button
            onClick={() => updateMutation.mutate({
              organizationId, enabled, folderId: trimmedFolder || null,
              overrides: Object.fromEntries(Object.entries(overrides).map(([code, value]) => [code, value.trim()])),
            })}
            disabled={saveDisabled}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}


function DesignsFolderOverrideRow({ code, storeName, folderId, organizationId, onChange, onRemove }: {
  code: string;
  storeName: string;
  folderId: string;
  organizationId?: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation('settings');
  const [tested, setTested] = useState<{ folderId: string; result: TestResult } | null>(null);
  const testMutation = trpc.productionSettings.testDesignsFolder.useMutation({
    onSuccess: (result, variables) => setTested({ folderId: variables.folderId, result }),
    onError: (error, variables) => {
      setTested({ folderId: variables.folderId, result: {
        ok: false, reason: error.data?.code === 'TOO_MANY_REQUESTS' ? 'rate_limited' : 'unreachable',
      } });
      toast.error(error.message);
    },
  });
  const trimmed = folderId.trim();
  const valid = FOLDER_ID_RE.test(trimmed);
  const result = tested?.folderId === trimmed ? tested.result : null;
  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label htmlFor={`designs-folder-${code}`} className="text-sm">
        <span className="font-mono">{code}</span>{storeName && <> · {storeName}</>}
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input id={`designs-folder-${code}`} value={folderId} className="min-w-0 flex-1 font-mono"
          aria-label={t('production.designsSource.folderForCode', { code })}
          aria-invalid={!valid || undefined} aria-describedby={`designs-folder-${code}-hint`}
          onChange={event => { setTested(null); onChange(event.target.value); }} />
        <Button type="button" variant="outline" size="sm" disabled={!valid || testMutation.isPending}
          aria-label={t('production.designsSource.testCode', { code })}
          onClick={() => { setTested(null); testMutation.mutate({ organizationId, folderId: trimmed }); }}>
          {testMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {t('production.designsSource.test')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}
          aria-label={t('production.designsSource.removeCode', { code })}>
          {t('production.designsSource.remove')}
        </Button>
      </div>
      <p id={`designs-folder-${code}-hint`} className="min-h-4 text-xs text-destructive">
        {!valid && t('production.designsSource.invalidFolder')}
      </p>
      {result && <p role="status" className="text-xs text-muted-foreground">
        {result.ok
          ? t('production.designsSource.testSample', { count: result.fileCount })
          : t(`production.designsSource.testFailure.${result.reason}`)}
      </p>}
    </div>
  );
}
